import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './PaymentResult.css';
import logo from '../assets/logo.png';

const PaymentResult = () => {
    const [params] = useSearchParams();
    const [secs, setSecs] = useState(10);

    const status  = params.get('status') || 'failed';
    const txnid   = params.get('txnid')  || '';
    const amount  = params.get('amount') || '';

    const isSuccess = status === 'success';
    const isPending = status === 'pending';
    const isFailed  = !isSuccess && !isPending;

    // Auto-redirect countdown → client login (they can log in to see their plan)
    useEffect(() => {
        if (secs <= 0) {
            window.location.href = '/client-login';
            return;
        }
        const t = setTimeout(() => setSecs(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [secs]);

    const goToDashboard = () => { window.location.href = '/client-login'; };
    const tryAgain      = () => { window.location.href = '/client-login'; };

    return (
        <div className="pr-page">
            {/* Background blobs */}
            <div className="pr-blob pr-blob--1" />
            <div className="pr-blob pr-blob--2" />

            <div className="pr-card">
                {/* Logo */}
                <div className="pr-logo">
                    <img src={logo} alt="Nimai Solutions" />
                    <span>Nimai Solutions</span>
                </div>

                {/* Animated Icon */}
                <div className={`pr-icon-wrap ${isSuccess ? 'pr-icon--success' : isPending ? 'pr-icon--pending' : 'pr-icon--failed'}`}>
                    {isSuccess && (
                        <svg viewBox="0 0 52 52" className="pr-checkmark">
                            <circle className="pr-checkmark__circle" cx="26" cy="26" r="25" fill="none" />
                            <path  className="pr-checkmark__check"  fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                        </svg>
                    )}
                    {isPending && (
                        <svg viewBox="0 0 52 52" className="pr-clock">
                            <circle cx="26" cy="26" r="24" fill="none" stroke="#f59e0b" strokeWidth="2.5"/>
                            <line x1="26" y1="14" x2="26" y2="26" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round"/>
                            <line x1="26" y1="26" x2="34" y2="32" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                    )}
                    {isFailed && (
                        <svg viewBox="0 0 52 52" className="pr-cross">
                            <circle cx="26" cy="26" r="24" fill="none" stroke="#ef4444" strokeWidth="2.5"/>
                            <line className="pr-cross__line" x1="16" y1="16" x2="36" y2="36" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round"/>
                            <line className="pr-cross__line" x1="36" y1="16" x2="16" y2="36" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round"/>
                        </svg>
                    )}
                </div>

                {/* Status Badge */}
                <div className={`pr-badge ${isSuccess ? 'pr-badge--success' : isPending ? 'pr-badge--pending' : 'pr-badge--failed'}`}>
                    {isSuccess ? '✓ Payment Confirmed' : isPending ? '⏳ Processing' : '✕ Payment Failed'}
                </div>

                {/* Title */}
                <h1 className="pr-title">
                    {isSuccess ? 'Thank You! 🎉' : isPending ? 'Almost There!' : 'Oops! Something Went Wrong'}
                </h1>

                {/* Message */}
                <p className="pr-message">
                    {isSuccess
                        ? 'Your payment has been received successfully. Your plan will be activated shortly and a confirmation email has been sent to your registered email address.'
                        : isPending
                        ? 'Your payment is currently being processed by the bank. Please do not retry — we will notify you via email once it is confirmed.'
                        : 'Your payment could not be completed. This may be due to a cancellation, incorrect details, or a temporary issue. No amount has been deducted from your account.'}
                </p>

                {/* Transaction Details Box */}
                {(txnid || amount) && (
                    <div className="pr-details">
                        <div className="pr-details-title">Transaction Details</div>
                        {txnid && (
                            <div className="pr-detail-row">
                                <span>Transaction ID</span>
                                <strong>{txnid}</strong>
                            </div>
                        )}
                        {amount && (
                            <div className="pr-detail-row">
                                <span>Amount</span>
                                <strong>₹{parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                            </div>
                        )}
                        <div className="pr-detail-row">
                            <span>Payment Status</span>
                            <strong className={`pr-status-text pr-status-text--${isSuccess ? 'success' : isPending ? 'pending' : 'failed'}`}>
                                {isSuccess ? '● Successful' : isPending ? '● Pending' : '● Failed'}
                            </strong>
                        </div>
                        <div className="pr-detail-row">
                            <span>Payment Gateway</span>
                            <strong>Easebuzz</strong>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="pr-actions">
                    {isSuccess ? (
                        <button className="pr-btn pr-btn--primary" onClick={goToDashboard}>
                            <span>Go to Dashboard</span>
                            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                        </button>
                    ) : (
                        <>
                            <button className="pr-btn pr-btn--primary" onClick={tryAgain}>
                                <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/></svg>
                                <span>Try Again</span>
                            </button>
                            <button className="pr-btn pr-btn--ghost" onClick={goToDashboard}>
                                Back to Dashboard
                            </button>
                        </>
                    )}
                </div>

                {/* Countdown */}
                <div className="pr-countdown">
                    <div className="pr-countdown-ring">
                        <svg viewBox="0 0 36 36">
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="2.5"/>
                            <circle
                                cx="18" cy="18" r="15.9" fill="none"
                                stroke={isSuccess ? '#22c55e' : isPending ? '#f59e0b' : '#ef4444'}
                                strokeWidth="2.5"
                                strokeDasharray={`${(secs / 10) * 100} 100`}
                                strokeLinecap="round"
                                transform="rotate(-90 18 18)"
                            />
                        </svg>
                        <span>{secs}</span>
                    </div>
                    <p>Redirecting to login in <strong>{secs}s</strong></p>
                </div>

                {/* Support */}
                {isFailed && (
                    <p className="pr-support">
                        Need help? Contact us at <a href="mailto:support@nimaisolutions.in">support@nimaisolutions.in</a>
                    </p>
                )}
            </div>
        </div>
    );
};

export default PaymentResult;
